import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/apiClient';
import { formatCurrency } from '../utils/formatters';
import './EditAddOnsModal.css';

interface AddOn {
  _id?: string;
  sourceAddOnId?: string;
  sourceType: 'predefined' | 'custom';
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
  createdBy?: 'client' | 'stylist';
  status?: 'approved' | 'pending_approval' | 'rejected';
}

interface AvailableAddOn {
  _id: string;
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
  category: string;
}

interface EditAddOnsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  currentAddOns: AddOn[];
  serviceCategory?: string;
  stylistServiceId: string; // StylistService ID to fetch add-ons for this specific service
  servicePrice: number;
  hasDownPayment: boolean;
  downPaymentAmount?: number;
  paymentStatus: string;
  userType: 'client' | 'stylist';
  editCount?: number;
  onSave: () => void;
}

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const EditAddOnsModal: React.FC<EditAddOnsModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  currentAddOns,
  serviceCategory,
  stylistServiceId,
  servicePrice,
  hasDownPayment,
  downPaymentAmount = 0,
  paymentStatus,
  userType,
  editCount = 0,
  onSave,
}) => {
  const [selectedAddOns, setSelectedAddOns] = useState<AddOn[]>(currentAddOns);
  const [availableAddOns, setAvailableAddOns] = useState<AvailableAddOn[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Calculate totals
  const currentTotal = servicePrice + currentAddOns.reduce((sum, addon) => 
    sum + (addon.priceCents / 100), 0
  );
  const newTotal = servicePrice + selectedAddOns.reduce((sum, addon) => 
    sum + (addon.priceCents / 100), 0
  );
  const priceDifference = newTotal - currentTotal;

  // Calculate remaining payment considering downpayment
  const getRemainingPayment = () => {
    if (paymentStatus === 'paid') {
      return 0; // Already paid in full
    }
    if (paymentStatus === 'partial') {
      // Remaining = new total - down payment already made
      return Math.max(0, newTotal - downPaymentAmount);
    }
    return newTotal; // No payment made yet
  };

  const remainingPayment = getRemainingPayment();

  const fetchAvailableAddOns = useCallback(async () => {
    if (!stylistServiceId) {
      console.log('[EditAddOnsModal] No stylist service ID provided');
      return;
    }

    try {
      setLoading(true);
      // Fetch add-ons for this specific service (StylistService)
      const response = await api.get(`/stylist-services/${stylistServiceId}/add-ons`);
      console.log('[EditAddOnsModal] Fetched service add-ons:', response.data.data);
      
      // Filter to only show active add-ons
      const addOns = (response.data.data || []).filter((addon: any) => addon.isActive);
      
      setAvailableAddOns(addOns);
    } catch (error) {
      console.error('[EditAddOnsModal] Error fetching available add-ons:', error);
      alert('Failed to load available add-ons');
    } finally {
      setLoading(false);
    }
  }, [stylistServiceId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedAddOns(currentAddOns);
      fetchAvailableAddOns();
    }
  }, [isOpen, currentAddOns, fetchAvailableAddOns]);

  const isAddOnSelected = (addOnId: string) => {
    return selectedAddOns.some(
      addon => addon.sourceAddOnId === addOnId || addon._id === addOnId
    );
  };

  const handleToggleAddOn = (availableAddOn: AvailableAddOn) => {
    const isSelected = isAddOnSelected(availableAddOn._id);
    
    if (isSelected) {
      // Remove it
      setSelectedAddOns(prev =>
        prev.filter(addon => 
          addon.sourceAddOnId !== availableAddOn._id && addon._id !== availableAddOn._id
        )
      );
    } else {
      // Add it
      const newAddOn: AddOn = {
        _id: undefined, // Will be assigned by backend
        sourceAddOnId: availableAddOn._id,
        sourceType: 'predefined',
        name: availableAddOn.name,
        description: availableAddOn.description,
        priceCents: availableAddOn.priceCents,
        durationMinutes: availableAddOn.durationMinutes,
        createdBy: userType,
        status: 'approved',
      };
      setSelectedAddOns(prev => [...prev, newAddOn]);
    }
  };

  const handleRemoveCurrentAddOn = (addOnId: string) => {
    setSelectedAddOns(prev => prev.filter(addon => addon._id !== addOnId));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Determine what changed
      const addOnsToAdd = selectedAddOns.filter(
        addon => !currentAddOns.find(current => current._id === addon._id)
      );
      const addOnsToRemove = currentAddOns.filter(
        current => !selectedAddOns.find(addon => addon._id === current._id)
      );

      // Remove add-ons first
      for (const addon of addOnsToRemove) {
        if (addon._id) {
          await api.delete(`/appointments/${appointmentId}/add-ons/${addon._id}`);
        }
      }

      // Add new add-ons
      if (addOnsToAdd.length > 0) {
        await api.patch(`/appointments/${appointmentId}/add-ons`, { 
          addOns: addOnsToAdd.map(addon => ({
            sourceAddOnId: addon.sourceAddOnId,
            sourceType: addon.sourceType,
            name: addon.name,
            description: addon.description,
            priceCents: addon.priceCents,
            durationMinutes: addon.durationMinutes,
          }))
        });
      }

      let message = 'Add-ons updated successfully.';
      if (priceDifference !== 0) {
        message += `\n\nPrice adjustment: ${priceDifference > 0 ? '+' : ''}${formatCurrency(Math.abs(priceDifference))}`;
        if (paymentStatus === 'partial') {
          message += `\nRemaining payment: ${formatCurrency(remainingPayment)}`;
        }
      }

      alert(message);
      onSave();
      onClose();
    } catch (error: any) {
      console.error('[EditAddOnsModal] Error saving add-ons:', error);
      alert(error.response?.data?.message || error.message || 'Failed to update add-ons');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (selectedAddOns.length !== currentAddOns.length) return true;
    return !selectedAddOns.every(addon => 
      currentAddOns.find(current => current._id === addon._id)
    );
  };

  if (!isOpen) return null;

  return (
    <div className="edit-addons-modal">
      <div className="edit-addons-modal__overlay" onClick={onClose} />
      <div className="edit-addons-modal__content">
        {/* Header */}
        <div className="edit-addons-modal__header">
          <h2 className="edit-addons-modal__title">Edit Add-ons</h2>
          <button
            className="edit-addons-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Edit Count Info (for clients) */}
        {userType === 'client' && (
          <div className="edit-addons-modal__edit-count">
            <InfoIcon />
            <span>
              Edits used: {editCount} of 2
              {editCount === 1 && ' (1 edit remaining)'}
              {editCount === 0 && ' (2 edits remaining)'}
            </span>
          </div>
        )}

        {/* Content */}
        <div className="edit-addons-modal__body">
          {/* Selected Add-ons */}
          {selectedAddOns.length > 0 && (
            <div className="edit-addons-modal__section">
              <h3 className="edit-addons-modal__section-title">Selected Add-ons</h3>
              <div className="edit-addons-modal__selected-list">
                {selectedAddOns.map((addon, index) => (
                  <div key={addon._id || index} className="edit-addons-modal__selected-item">
                    <div className="edit-addons-modal__addon-info">
                      <div className="edit-addons-modal__addon-header">
                        <span className="edit-addons-modal__addon-name">{addon.name}</span>
                      </div>
                      {addon.description && (
                        <p className="edit-addons-modal__addon-description">{addon.description}</p>
                      )}
                      <div className="edit-addons-modal__addon-meta">
                        {addon.durationMinutes > 0 && (
                          <span className="edit-addons-modal__addon-duration">
                            <TimeIcon /> +{addon.durationMinutes} min
                          </span>
                        )}
                        <span className="edit-addons-modal__addon-price">
                          {formatCurrency(addon.priceCents / 100)}
                        </span>
                      </div>
                    </div>
                    <button
                      className="edit-addons-modal__remove-btn"
                      onClick={() => addon._id ? handleRemoveCurrentAddOn(addon._id) : handleToggleAddOn(addon as any)}
                      aria-label="Remove add-on"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Add-ons */}
          <div className="edit-addons-modal__section">
            <h3 className="edit-addons-modal__section-title">Available Add-ons</h3>
            {loading ? (
              <div className="edit-addons-modal__loading">
                <div className="spinner"></div>
                <p>Loading available add-ons...</p>
              </div>
            ) : availableAddOns.length === 0 ? (
              <p className="edit-addons-modal__empty">No add-ons available for this service</p>
            ) : (
              <div className="edit-addons-modal__available-list">
                {availableAddOns.map((addon) => {
                  const isSelected = isAddOnSelected(addon._id);
                  return (
                    <div
                      key={addon._id}
                      className={`edit-addons-modal__available-item ${isSelected ? 'edit-addons-modal__available-item--selected' : ''}`}
                      onClick={() => handleToggleAddOn(addon)}
                    >
                      <div className="edit-addons-modal__addon-info">
                        <span className="edit-addons-modal__addon-name">{addon.name}</span>
                        {addon.description && (
                          <p className="edit-addons-modal__addon-description">{addon.description}</p>
                        )}
                        <div className="edit-addons-modal__addon-meta">
                          {addon.durationMinutes > 0 && (
                            <span className="edit-addons-modal__addon-duration">
                              <TimeIcon /> +{addon.durationMinutes} min
                            </span>
                          )}
                          <span className="edit-addons-modal__addon-price">
                            {formatCurrency(addon.priceCents / 100)}
                          </span>
                        </div>
                      </div>
                      <div className={`edit-addons-modal__checkbox ${isSelected ? 'edit-addons-modal__checkbox--selected' : ''}`}>
                        {isSelected && <CheckIcon />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Price Summary */}
          <div className="edit-addons-modal__price-summary">
            <h3 className="edit-addons-modal__price-title">Price Summary</h3>
            <div className="edit-addons-modal__price-row">
              <span>Service</span>
              <span>{formatCurrency(servicePrice)}</span>
            </div>
            {selectedAddOns.length > 0 && (
              <div className="edit-addons-modal__price-row">
                <span>Add-ons ({selectedAddOns.length})</span>
                <span>
                  {formatCurrency(selectedAddOns.reduce((sum, addon) => 
                    sum + (addon.priceCents / 100), 0
                  ))}
                </span>
              </div>
            )}
            {priceDifference !== 0 && (
              <div className={`edit-addons-modal__price-row ${priceDifference > 0 ? 'edit-addons-modal__price-row--increase' : 'edit-addons-modal__price-row--decrease'}`}>
                <span>{priceDifference > 0 ? 'Additional Charge' : 'Refund'}</span>
                <span>
                  {priceDifference > 0 ? '+' : ''}{formatCurrency(priceDifference)}
                </span>
              </div>
            )}
            <div className="edit-addons-modal__price-divider" />
            <div className="edit-addons-modal__price-row edit-addons-modal__price-row--total">
              <span>New Total</span>
              <span>{formatCurrency(newTotal)}</span>
            </div>
            {paymentStatus === 'partial' && (
              <>
                <div className="edit-addons-modal__price-divider" />
                <div className="edit-addons-modal__price-row">
                  <span>Already charged</span>
                  <span>-{formatCurrency(downPaymentAmount)}</span>
                </div>
                <div className="edit-addons-modal__price-row edit-addons-modal__price-row--remaining">
                  <span>Remaining Payment</span>
                  <span>{formatCurrency(remainingPayment)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="edit-addons-modal__footer">
          <button
            className="edit-addons-modal__btn edit-addons-modal__btn--cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className={`edit-addons-modal__btn edit-addons-modal__btn--save ${!hasChanges() || saving ? 'edit-addons-modal__btn--disabled' : ''}`}
            onClick={handleSave}
            disabled={!hasChanges() || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAddOnsModal;
